// src/services/api.js
import axios from 'axios';

// ============================================================
// BASE URL CONFIGURATION
// ============================================================
const getBaseURL = () => {
  // Priorité 1: Variable d'environnement
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  
  // Priorité 2: Détection automatique selon l'environnement
  const isDev = process.env.NODE_ENV === 'development';
  const hostname = window.location.hostname;
  
  if (isDev) {
    // En dev local, utiliser localhost:5000
    return 'http://localhost:5000/api';
  }
  
  // En production : deduire le backend depuis le frontend.
  // app2.maxula.ma -> https://api2.maxula.ma/api
  if (hostname.startsWith('app2.')) {
    return `https://${hostname.replace(/^app2\./, 'api2.')}/api`;
  }
  if (hostname.startsWith('app.')) {
    return `https://${hostname.replace(/^app\./, 'api.')}/api`;
  }
  // deals.maxula.ma : le backend est servi sur le MÊME domaine (dossier /server via
  // Passenger + .htaccess), donc l'API est sur https://deals.maxula.ma/api.
  if (hostname === 'deals.maxula.ma' || hostname === 'www.deals.maxula.ma') {
    return `https://${hostname.replace(/^www\./, '')}/api`;
  }
  if (hostname === 'maxula.ma' || hostname === 'www.maxula.ma') {
    return 'https://api2.maxula.ma/api';
  }
  // Tout autre hôte maxula.ma (sous-domaines de test, etc.) -> API prod app2/api2
  if (hostname.endsWith('.maxula.ma')) {
    return 'https://api2.maxula.ma/api';
  }
  
  // Fallback
  return '/api';
};

const baseURL = getBaseURL();

if (process.env.NODE_ENV === 'development') {
  // AVERTISSEMENT VISIBLE — ne pas retirer.
  //
  // Le fichier `.env` de ce projet contient l'URL de PRODUCTION. Create React
  // App le lit AUSSI pendant `npm start` : sans `.env.development` pour le
  // couvrir, localhost:3000 envoie toutes ses requetes a deals.maxula.ma.
  // Un correctif deploye en local ne change alors strictement rien a l'ecran,
  // et rien ne le signale — on croit que le correctif ne marche pas.
  const local = /localhost|127\.0\.0\.1/.test(baseURL);
  console.log(
    `%c API -> ${baseURL} `,
    `background:${local ? '#0a7d3f' : '#b91c1c'};color:#fff;font-weight:bold;padding:2px 6px;`
  );
  if (!local) {
    console.warn(
      `[Maxula] Vous developpez sur localhost mais l'API pointe sur ${baseURL} (SERVEUR DE PRODUCTION).\n` +
      `Creez client/.env.development avec :  REACT_APP_API_URL=http://localhost:5000/api\n` +
      `puis relancez npm start (les variables ne sont lues qu'au demarrage).`
    );
  }
}

const API = axios.create({
  baseURL,
  headers: { Accept: 'application/json' },
  timeout: 30000,
  withCredentials: true
});

// ============================================================
// TOKEN EXTRACTION
// ============================================================
const extractToken = () => {
  try {
    const raw = localStorage.getItem('token');
    if (raw) return raw;
  } catch {}

  try {
    const stored = localStorage.getItem('auth') || localStorage.getItem('user');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.token || parsed?.accessToken || parsed?.jwt || null;
    }
  } catch {}

  return null;
};

const isObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

// ============================================================
// REQUEST INTERCEPTOR
// ============================================================
API.interceptors.request.use(
  (config) => {
    const token = extractToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Fix double /api/api/
    const base = config.baseURL || '';
    const url = config.url || '';
    if (base.endsWith('/api') && url.startsWith('/api/')) {
      config.url = url.replace(/^\/api/, '');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================
// RESPONSE INTERCEPTOR
// ============================================================
API.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================
// API ENDPOINTS
// ============================================================
export const pageAPI = {
  menu: () => API.get('/pages/menu'),
  show: (slug) => API.get('/pages/show', { params: { slug } }),
  adminList: () => API.get('/pages/admin'),
  adminGet: (id) => API.get(`/pages/admin/${id}`),
  adminCreate: (data) => API.post('/pages/admin', data),
  adminUpdate: (id, data) => API.put(`/pages/admin/${id}`, data),
  adminDelete: (id) => API.delete(`/pages/admin/${id}`)
};

export const cguAPI = {
  status: () => API.get('/cgu/status'),
  accept: (audience) => API.post('/cgu/accept', audience ? { audience } : {}),
  adminRegistry: (params) => API.get('/cgu/admin/registry', { params })
};

export const authAPI = {
  login: (data) => API.post('/auth/login', data),
  refresh: () => API.post('/auth/refresh'),
  register: (data) => API.post('/auth/register', data),
  getMe: async () => {
    const candidates = ['/auth/me', '/users/me', '/me'];
    let lastErr;
    for (const ep of candidates) {
      try {
        return await API.get(ep);
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr;
  },
  forgotPassword: (data) => API.post('/auth/forgot-password', data),
  resetPassword: (token, data) => API.put(`/auth/reset-password/${token}`, data),
  verifyEmail: (token) => API.post('/auth/verify-email', { token }),
  resendVerification: () => API.post('/auth/resend-verification'),
  setup2FA: () => API.post('/auth/2fa/setup'),
  verify2FA: (token) => API.post('/auth/2fa/verify', { token }),
  disable2FA: (token) => API.post('/auth/2fa/disable', { token })
};

export const dealAPI = {

  // COMPTEUR DE VUES. Cette methode N'EXISTAIT PAS, alors que la route
  // serveur (POST /deals/slug/:slug/view) et l'appel dans DealDetail.js
  // existaient depuis toujours. L'appel etait protege par
  // `typeof dealAPI.incrementViews === 'function'` : la fonction absente,
  // la condition etait fausse et rien ne se passait, EN SILENCE. Le
  // compteur affichait donc 0 depuis le premier jour.
  incrementViews: (slug) => API.post(`/deals/slug/${slug}/view`),
  getAll: (params) => API.get('/deals', { params }),
  getOne: (slugOrId) =>
    isObjectId(slugOrId)
      ? API.get(`/deals/id/${slugOrId}`)
      : API.get(`/deals/slug/${encodeURIComponent(slugOrId)}`),
  // v1.418.0 — la route accepte desormais des parametres de rotation :
  //   { limit, productType, window (minutes), rotate: 0|1 }
  // Sans parametre, le serveur applique les reglages Admin > Parametres.
  getFeatured: (params) => API.get('/deals/featured', { params }),
  getByCategory: (slugOrId) =>
    isObjectId(slugOrId)
      ? API.get(`/deals/category/${slugOrId}`)
      : API.get(`/deals/category/${encodeURIComponent(slugOrId)}`),
  create: (data) => API.post('/deals', data),
  createGuest: (data) => API.post('/deals/guest', data),
  update: (id, data) => API.put(`/deals/id/${id}`, data),
  delete: (id) => API.delete(`/deals/id/${id}`)
};

export const orderAPI = {

  // Liste du personnel, pour choisir a QUI la vente est creditee quand un
  // commercial saisit a la place d'un collegue absent.
  // Route DEDIEE et non /admin/staff : celle-la est reservee a l'admin et
  // renvoyait 403 a un commercial — le menu restait donc invisible pour ceux
  // qui en ont justement besoin. Celle-ci ne renvoie que les noms.
  staffList: () => API.get('/orders/sales-reps'),
  // REMBOURSEMENTS. La demande est ouverte au personnel ; la decision et
  // l'annulation sont refusees par le SERVEUR a tout compte non-admin.
  requestRefund: (id, reason) => API.post(`/orders/${id}/refund-request`, { reason }),
  decideRefund: (id, approve, note) => API.put(`/orders/${id}/refund-decision`, { approve, note }),
  undoRefund: (id, note) => API.put(`/orders/${id}/refund-undo`, { note }),
  create: (data) => API.post('/orders', data),
  createGuest: (data) => API.post('/orders/guest', data),
  getAll: (params) => API.get('/orders', { params }),
  getOne: (id) => API.get(`/orders/${id}`),
  // v1.444.0 — retirer une commande de la liste
  // v1.459.0 — `removable` supprimé : le verdict arrive désormais AVEC la
  // liste des commandes. Un appel de moins, un point de panne de moins.
  //
  // POST et non PATCH : POST est la méthode utilisée partout ailleurs dans
  // l'application et qui fonctionne de façon prouvée chez vous. PATCH n'était
  // employée que par l'archivage — autant ne pas dépendre d'une méthode qui
  // n'a jamais servi ailleurs.
  // v1.460.0 — PUT d'abord, POST en secours.
  //
  // PUT /admin/orders/:id/status fonctionne chez tous les clients (c'est lui
  // qui change le statut d'une commande au quotidien). On emprunte donc la
  // meme methode sur une route de meme forme.
  //
  // Si la requete n'obtient AUCUNE reponse — rejetee en amont par un module de
  // securite ou une extension — on retente en POST. Un seul essai
  // supplementaire, uniquement quand rien n'est revenu : une erreur metier
  // (403, 409...) n'est jamais rejouee.
  // v1.461.0 — CHEMIN NEUTRE D'ABORD, anciens chemins en secours.
  //
  // PUT, POST et PATCH echouent tous en 0,0 s sur `/archive` : ce n'est donc
  // pas la methode, c'est le MOT dans l'URL. Les modules de securite
  // d'hebergement (ModSecurity) et plusieurs bloqueurs de navigateur filtrent
  // « archive » et « delete ».
  //
  // On tente les chemins dans l'ordre, et UNIQUEMENT tant qu'aucune reponse
  // n'est revenue. Une erreur metier (403, 409, « commande remboursee »...)
  // arrete immediatement la serie : elle doit remonter telle quelle.
  setArchived: async (id, archived, reason = '') => {
    const corps = { archived, reason };
    const essais = [
      () => API.put(`/admin/orders/${id}/etat-liste`, corps),
      () => API.post(`/admin/orders/${id}/etat-liste`, corps),
      () => API.put(`/admin/orders/${id}/archive`, corps),
      () => API.post(`/admin/orders/${id}/archive`, corps),
    ];
    let derniere;
    for (const essai of essais) {
      try { return await essai(); }
      catch (e) { if (e && e.response) throw e; derniere = e; }
    }
    throw derniere;
  },
  // La suppression exige le mot SUPPRIMER et un motif : ce sont les
  // garde-fous du serveur, le client ne fait que les transmettre.
  // Meme strategie pour la suppression.
  // Meme strategie pour la suppression definitive.
  remove: async (id, reason) => {
    const corps = { confirm: 'SUPPRIMER', reason };
    const essais = [
      () => API.put(`/admin/orders/${id}/retrait-definitif`, corps),
      () => API.post(`/admin/orders/${id}/retrait-definitif`, corps),
      () => API.put(`/admin/orders/${id}/delete`, corps),
      () => API.post(`/admin/orders/${id}/delete`, corps),
    ];
    let derniere;
    for (const essai of essais) {
      try { return await essai(); }
      catch (e) { if (e && e.response) throw e; derniere = e; }
    }
    throw derniere;
  },
  updateStatus: (id, status) => API.put(`/orders/${id}/status`, { status }),
  // MODIFICATION D'UNE COMMANDE.
  // `editable` est interroge AVANT d'ouvrir le formulaire : l'ecran sait ainsi
  // ce qu'il peut proposer, au lieu de laisser l'agent tout ressaisir pour
  // recevoir un refus a l'enregistrement.
  editable: (id) => API.get(`/orders/${id}/editable`),
  updateOrder: (id, data) => API.put(`/orders/${id}`, data),
  createManual: (data) => API.post('/orders/manual', data),
  // PAYEXPRESS AU GUICHET — la commande est creee NON PAYEE, puis on demande
  // le code que le commercial transmet au client. Idempotent cote serveur :
  // rappeler la route sur la meme commande renvoie le MEME code, jamais un
  // second (deux codes pour une commande = un client qui paie le mauvais).
  payexpressCode: (id) => API.post(`/orders/${id}/payexpress-code`),
  sendPayexpressCode: (id, data) => API.post(`/orders/${id}/payexpress-code/send`, data),
  cancel: (id) => API.put(`/orders/${id}/cancel`),
  confirmReception: (id) => API.put(`/orders/${id}/confirm-reception`),
  logDigitalAccess: (id) => API.put(`/orders/${id}/digital-access`),
  uploadPaymentProof: (id, formData, guestToken) =>
    API.post(`/orders/${id}/payment-proof`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(guestToken ? { 'x-guest-token': guestToken } : {})
      }
    })
};

export const disputeAPI = {
  open: (id, data) => API.post(`/orders/${id}/dispute`, data),
  adminList: (params) => API.get('/admin/disputes', { params }),
  adminDecide: (id, data) => API.patch(`/admin/disputes/${id}`, data),
};

// LOT B : suivi public via lien magique (token) — sans compte
export const trackAPI = {
  get: (orderNumber, t) => API.get(`/orders/track/${encodeURIComponent(orderNumber)}`, { params: { t } }),
  confirm: (orderNumber, t) => API.post(`/orders/track/${encodeURIComponent(orderNumber)}/confirm`, { t }, { params: { t } }),
  dispute: (orderNumber, t, data) => API.post(`/orders/track/${encodeURIComponent(orderNumber)}/dispute`, { ...data, t }, { params: { t } }),
};

export const userAPI = {
  getProfile: () => API.get('/users/profile'),
  updateProfile: (data) => API.put('/users/profile', data),
  updateMedia: (formData) => API.put('/users/profile/media', formData),
  updatePassword: (data) => API.put('/users/password', data),
  getOrders: () => API.get('/users/orders'),
  getVouchers: (status) => API.get('/users/vouchers', { params: { status } }),
  getFavorites: () => API.get('/users/favorites'),
  addFavorite: (dealId) => API.post(`/users/favorites/${dealId}`),
  removeFavorite: (dealId) => API.delete(`/users/favorites/${dealId}`)
};

export const categoryAPI = {
  getAll: () => API.get('/categories'),
  getOne: (slugOrId) =>
    isObjectId(slugOrId)
      ? API.get(`/categories/${slugOrId}`)
      : API.get(`/categories/slug/${encodeURIComponent(slugOrId)}`)
};

// Marques du catalogue. `getAll` renvoie desormais `dealsCount` (nombre
// d'annonces VISIBLES) pour l'affichage sur la page d'accueil.
export const brandAPI = {
  getAll: (params) => API.get('/brands', { params }),
  create: (data, config) => API.post('/brands', data, config),
  update: (id, data, config) => API.put(`/brands/${id}`, data, config),
  remove: (id) => API.delete(`/brands/${id}`),
};

export const cityAPI = {
  getAll: (params) => API.get('/cities', { params }),
  getOne: (slug) => API.get(`/cities/slug/${slug}`),
  // `config` accepte l'en-tete multipart quand une photo est jointe.
  create: (data, config) => API.post('/cities', data, config),
  update: (id, data, config) => API.put(`/cities/${id}`, data, config),
  delete: (id) => API.delete(`/cities/${id}`),
  toggle: (id) => API.patch(`/cities/${id}/toggle`)
};


export const adminAPI = {
  updateUserRole: (id, data) => API.patch(`/admin/users/${id}/role`, data),
  getDashboard: () => API.get('/admin/dashboard'),
  getAnalytics: (params) => API.get('/admin/analytics', { params }),
  getUsers: (params) => API.get('/admin/users', { params }),
  getUserStats: () => API.get('/admin/users/stats'),
  updateUserStatus: (id, data) => API.patch(`/admin/users/${id}/status`, data),
  getMerchants: (params) => API.get('/admin/merchants', { params }),
  approveMerchant: (id) => API.put(`/admin/merchants/${id}/approve`),
  suspendMerchant: (id) => API.put(`/admin/merchants/${id}/suspend`),
  getDeals: (params) => API.get('/admin/deals', { params }),
  approveDeal: (id) => API.put(`/admin/deals/${id}/approve`),
  updateDealStatus: (id, data) => API.patch(`/admin/deals/${id}/status`, data),
  deleteDeal: (id) => API.delete(`/admin/deals/${id}`),
  getOrders: (params) => API.get('/admin/orders', { params }),
  getBadges: () => API.get('/admin/badges'),
  moderationFlags: (params = {}) => API.get('/admin/moderation/flags', { params }),
  moderationSummary: () => API.get('/admin/moderation/summary'),
  reviewModerationFlag: (id, reviewed = true) => API.post(`/admin/moderation/flags/${id}/review`, { reviewed }),
  reviewModerationConversation: (conversationId) => API.post(`/admin/moderation/conversation/${conversationId}/review`),
  // Phase 4.5 — restriction / réinitialisation des récidivistes
  setMerchantRestriction: (merchantId, restricted) => API.post(`/admin/moderation/merchant/${merchantId}/restriction`, { restricted }),
  resetMerchantStrikes: (merchantId) => API.post(`/admin/moderation/merchant/${merchantId}/reset`),
  getReports: (params) => API.get('/admin/reports', { params }),
  getReviews: (params) => API.get('/admin/reviews', { params }),
  approveReview: (id) => API.patch(`/admin/reviews/${id}/approve`),
  rejectReview: (id) => API.patch(`/admin/reviews/${id}/reject`),
  deleteReview: (id) => API.delete(`/admin/reviews/${id}`),
  getNotifications: (params) => API.get('/admin/notifications', { params }),
  getUnreadNotificationsCount: (params) =>
    API.get('/admin/notifications/unread-count', { params }),
  markAsRead: (id) => API.patch(`/admin/notifications/${id}/read`),
  markAllAsRead: (params) =>
    API.patch('/admin/notifications/read-all', null, { params }),
  deleteNotification: (id) => API.delete(`/admin/notifications/${id}`),
  deleteReadNotifications: (params) =>
    API.delete('/admin/notifications', { params })
};

export const merchantAPI = {
  getAll: (params) => API.get('/merchants', { params }),
  getOne: (id) => API.get(`/merchants/${id}`),
  // 4.4 — Coordonnées du partenaire révélées après un achat payé.
  getContact: (id) => API.get(`/merchants/${id}/contact`),
  create: (data) => API.post('/merchant/register', data),
  update: (id, data) => API.put(`/merchants/${id}`, data),
  verify: (id) => API.put(`/merchants/${id}/verify`),
  getCurrentDeals: () => API.get('/merchant/me/deals'),
  renewDeal: (id, days = 30) => API.put(`/merchant/me/deals/${id}/renew`, { days }),
  // Duplication d'une annonce (la copie part en attente de validation)
  duplicateDeal: (id, data = {}) => API.post(`/merchant/me/deals/${id}/duplicate`, data),
  getOrders: (params) => API.get('/merchant/me/orders', { params }),
  getBadges: () => API.get('/merchant/me/badges'),
  confirmOrderPayment: (id) => API.put(`/merchant/me/orders/${id}/confirm-payment`),
  updateOrderDelivery: (id, data) => API.put(`/merchant/me/orders/${id}/delivery`, data),
  deliverDigital: (id, data) => API.put(`/merchant/me/orders/${id}/digital-deliver`, data),
  getCurrentDashboard: () => API.get('/merchant/dashboard'),
  getProfile: () => API.get('/merchant/me/profile'),
  updateProfile: (data) => API.put('/merchant/me/profile', data),
  updateMedia: (formData) => API.put('/merchant/me/media', formData),
  charts: () => API.get('/merchant/me/charts'),
  // Lieux de prestation reutilisables (adresse + horaires saisis une seule fois)
  getServiceLocations: () => API.get('/merchant/me/service-locations'),
  saveServiceLocations: (serviceLocations) => API.put('/merchant/me/service-locations', { serviceLocations }),
  applyServiceLocation: (data) => API.post('/merchant/me/service-locations/apply', data),
  updatePaymentAccount: (data) => API.put('/merchant/me/payment-account', data),
  myPermissions: () => API.get('/merchant/me/permissions'),
  listTeam: () => API.get('/merchant/me/team'),
  addMember: (data) => API.post('/merchant/me/team', data),
  updateMember: (id, data) => API.put(`/merchant/me/team/${id}`, data),
  removeMember: (id) => API.delete(`/merchant/me/team/${id}`)
};

export const paymentAPI = {
  createPayment: (data) => API.post('/payment/initiate', data),
  verifyPayment: (data) => API.post('/payment/verify', data),
  getPaymentStatus: (orderId) => API.post('/payment/verify', { orderId })
};

export const reviewAPI = {
  create: (data) => API.post('/reviews', data),
  getByDeal: (dealId) => API.get(`/reviews/deal/${dealId}`),
  update: (id, data) => API.put(`/reviews/${id}`, data),
  delete: (id) => API.delete(`/reviews/${id}`)
};

// Notifications (tous rôles : admin / marchand / client)
export const notifAPI = {
  list: (params) => API.get('/notifications', { params }),
  unreadCount: () => API.get('/notifications/unread-count'),
  markRead: (id) => API.patch(`/notifications/${id}/read`),
  markAllRead: () => API.patch('/notifications/read-all'),
  remove: (id) => API.delete(`/notifications/${id}`)
};

// Codes promo / réductions
export const promoAPI = {
  list: () => API.get('/promos'),
  create: (data) => API.post('/promos', data),
  update: (id, data) => API.put(`/promos/${id}`, data),
  remove: (id) => API.delete(`/promos/${id}`),
  apply: (payload) => API.post('/promos/apply', payload)
};

// Récompenses post-achat (v1.65.0)
export const rewardAPI = {
  list: () => API.get('/rewards'),                 // règles (admin/marchand)
  create: (data) => API.post('/rewards', data),
  update: (id, data) => API.put(`/rewards/${id}`, data),
  remove: (id) => API.delete(`/rewards/${id}`),
  mine: () => API.get('/rewards/mine')             // codes gagnés (client)
};

export const voucherAPI = {
  getAll: (params) => API.get('/vouchers', { params }),
  getOne: (code) => API.get(`/vouchers/${encodeURIComponent(code)}`),
  use: (code) => API.post(`/vouchers/${encodeURIComponent(code)}/use`),
  validate: (code, location) => API.get(`/vouchers/validate/${encodeURIComponent(code)}`, { params: location ? { location } : {} }),
  // v1.397.0 — annuler une validation faite par erreur (admin / mode support)
  unvalidate: (code, scope = 'single', reason) => API.post(`/vouchers/${encodeURIComponent(code)}/unvalidate`, { scope, reason }),
  // v1.396.0 — valider tous les coupons actifs d'une commande en une fois
  validateOrder: (orderId, location) => API.post(`/vouchers/validate-order/${encodeURIComponent(orderId)}`, location ? { location } : {}),
  merchantMine: (params) => API.get('/vouchers/merchant/mine', { params: (params && typeof params === 'object') ? params : (params ? { status: params } : {}) }),
  adminList: (params) => API.get('/vouchers', { params })
};

// --- Packs d'abonnement (multivendeur) ---
export const planAPI = {
  getPublic: (type) => API.get('/plans', { params: type ? { type } : {} }),
  getAllAdmin: () => API.get('/plans/admin/all'),
  getOne: (id) => API.get(`/plans/${id}`),
  create: (data) => API.post('/plans', data),
  update: (id, data) => API.put(`/plans/${id}`, data),
  remove: (id) => API.delete(`/plans/${id}`)
};

export const subscriptionAPI = {
  getMine: () => API.get('/subscriptions/me'),
  subscribe: (data) => API.post('/subscriptions/subscribe', data),
  getAllAdmin: (status) => API.get('/subscriptions/admin/all', { params: status ? { status } : {} }),
  saasStats: () => API.get('/subscriptions/admin/saas-stats'),
  activate: (id) => API.put(`/subscriptions/${id}/activate`),
  cancel: (id, reason) => API.put(`/subscriptions/${id}/cancel`, { reason }),
  extend: (id, days) => API.put(`/subscriptions/${id}/extend`, { days }),
  uploadProof: (id, formData) => API.post(`/subscriptions/${id}/proof`, formData),
  remove: (id) => API.delete(`/subscriptions/${id}`),
  cleanupPending: () => API.delete('/subscriptions/admin/cleanup-pending')
};

// --- Paramètres SaaS (whitelabel) ---
export const settingsAPI = {
  get: () => API.get('/settings'),
  update: (data) => API.put('/settings', data),
  contact: (data) => API.post('/settings/contact', data)
};

// --- KYC / Vérifications + Activité ---
export const verificationAPI = {
  getMyKyc: () => API.get('/verifications/me'),
  submit: (formData) => API.post('/verifications/submit', formData),
  adminList: (status) => API.get('/verifications/admin', { params: status ? { status } : {} }),
  approve: (id) => API.put(`/verifications/${id}/approve`),
  reject: (id, reason) => API.put(`/verifications/${id}/reject`, { reason }),
  myActivity: () => API.get('/verifications/activity/me'),
  adminActivity: (params) => API.get('/verifications/activity/admin', { params })
};

// --- Factures ---
export const invoiceAPI = {
  getMine: () => API.get('/invoices/me'),
  adminList: () => API.get('/invoices/admin'),
  orderInvoice: (orderNumber) => API.get(`/invoices/order/${encodeURIComponent(orderNumber)}`),
  orderInvoicePdfBlob: (orderNumber) => API.get(`/invoices/order/${encodeURIComponent(orderNumber)}/pdf`, { responseType: 'blob' }),
  // Partage d'une commande par email (admin) : recap + lien du reçu PDF
  sendOrderByEmail: (orderNumber, data = {}) => API.post(`/invoices/order/${encodeURIComponent(orderNumber)}/send-email`, data),
  // v1.398.0 — reemettre les coupons depuis le contenu actuel de la commande (admin)
  reissueCoupons: (orderNumber) => API.post(`/invoices/order/${encodeURIComponent(orderNumber)}/reissue-coupons`),
  downloadPdf: (id) => API.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  // Décompte de commission (marchand : le sien) — PDF blob (route authentifiée)
  commissionStatementBlob: (params = {}) => API.get('/invoices/commission/me/pdf', { params, responseType: 'blob' }),
  commissionStatementAdminBlob: (merchantId, params = {}) => API.get(`/invoices/commission/${merchantId}/pdf`, { params, responseType: 'blob' }),
  adminExport: () => API.get('/invoices/admin/export', { responseType: 'blob' })
};

// --- Wallet marchand ---
// P1-B (v1.154) : recherche avancée + autocomplétion
// P2-C (v1.158) : parrainage
export const referralAPI = {
  getMine: () => API.get('/referrals/me'),
  adminList: () => API.get('/referrals/admin')
};

// P1-C (v1.155) : demandes de remboursement
export const refundAPI = {
  request: (orderId, data) => API.post(`/orders/${orderId}/refund-request`, data),
  adminList: (params) => API.get('/admin/refund-requests', { params }),
  adminDecide: (id, data) => API.patch(`/admin/refund-requests/${id}`, data)
};

export const searchAPI = {
  search: (params) => API.get('/search', { params }),
  suggestions: (q) => API.get('/search/suggestions', { params: { q } })
};

export const walletAPI = {
  getMine: () => API.get('/wallet/me'),
  // v1.419.0 — la demande porte desormais le mode de reglement choisi
  // (bank_transfer | cash | cheque) et une note libre facultative.
  // L'ancienne signature withdraw(montant) reste acceptee : le serveur
  // retombe sur le virement par defaut.
  withdraw: (amount, paymentMethod = 'bank_transfer', note = '') =>
    API.post('/wallet/withdraw', { amount, paymentMethod, note }),
  // Modes disponibles + date prevue si la demande etait faite aujourd'hui.
  payoutOptions: () => API.get('/wallet/payout-options'),
  cancelWithdraw: (id) => API.put(`/wallet/withdraw/${id}/cancel`),
  getEarnings: () => API.get('/wallet/earnings'),
  getMerchantEarnings: (id) => API.get(`/wallet/admin/merchants/${id}/earnings`),
  exportEarnings: (month, format) => API.get('/wallet/exports/earnings', { params: { month, format }, responseType: 'blob' }),
  exportWithdrawals: (year, format) => API.get('/wallet/exports/withdrawals', { params: { year, format }, responseType: 'blob' }),
  adminWithdrawals: (status) => API.get('/wallet/admin/withdrawals', { params: status ? { status } : {} }),
  // v1.419.0 — a la validation, l'admin transmet la date REELLE du reglement
  // et sa reference (numero de cheque, recu especes, avis de virement).
  process: (id, action, extra = {}) =>
    API.put(`/wallet/admin/withdrawals/${id}`, { action, ...extra }),
  adminWithdrawalsFiltered: (status, method) =>
    API.get('/wallet/admin/withdrawals', { params: { ...(status ? { status } : {}), ...(method ? { method } : {}) } })
};

// --- Tickets support ---
export const ticketAPI = {
  create: (data) => API.post('/tickets', data),
  getMine: () => API.get('/tickets/me'),
  getOne: (id) => API.get(`/tickets/${id}`),
  reply: (id, body) => API.post(`/tickets/${id}/reply`, { body }),
  adminList: (status) => API.get('/tickets/admin', { params: status ? { status } : {} }),
  setStatus: (id, status) => API.put(`/tickets/${id}/status`, { status })
};

// --- Signalements ---
export const reportAPI = {
  create: (data) => API.post('/reports', data),
  adminList: (status) => API.get('/reports/admin', { params: status ? { status } : {} }),
  update: (id, data) => API.put(`/reports/${id}`, data)
};

// --- Leads ---
export const leadAPI = {
  create: (data) => API.post('/leads', data),
  getMine: () => API.get('/leads/me'),
  adminList: () => API.get('/leads/admin'),
  setStatus: (id, status) => API.put(`/leads/${id}/status`, { status })
};

// --- Publicités ---
export const adAPI = {
  getActive: (placement) => API.get('/ads', { params: placement ? { placement } : {} }),
  trackClick: (id) => API.post(`/ads/${id}/click`),
  adminList: () => API.get('/ads/admin/all'),
  create: (data) => API.post('/ads', data),
  update: (id, data) => API.put(`/ads/${id}`, data),
  remove: (id) => API.delete(`/ads/${id}`)
};

// --- Developer (API keys + webhooks) ---
export const developerAPI = {
  listKeys: () => API.get('/developer/keys'),
  createKey: (data) => API.post('/developer/keys', data),
  revokeKey: (id) => API.delete(`/developer/keys/${id}`),
  listWebhooks: () => API.get('/developer/webhooks'),
  createWebhook: (data) => API.post('/developer/webhooks', data),
  deleteWebhook: (id) => API.delete(`/developer/webhooks/${id}`),
  testWebhook: () => API.post('/developer/webhooks/test')
};

export const marketingAPI = {
  audience: () => API.get('/marketing/audience'),
  listCampaigns: () => API.get('/marketing/campaigns'),
  sendCampaign: (data) => API.post('/marketing/campaigns', data)
};

export const chatAPI = {
  list: (as) => API.get('/conversations', { params: as ? { as } : {} }),
  start: (data) => API.post('/conversations', data),
  startAsMerchant: (data) => API.post('/conversations', { ...data, as: 'merchant' }),
  messages: (id) => API.get(`/conversations/${id}/messages`),
  send: (id, text) => API.post(`/conversations/${id}/messages`, { text }),
  unreadCount: () => API.get('/conversations/unread-count')
};

export const metricsAPI = {
  // Fire-and-forget : ne jamais bloquer l'UX (toujours .catch côté appelant).
  logLeak: (data) => API.post('/metrics/leak', data),
  leakage: (params) => API.get('/metrics/admin/leakage', { params })
};

export default API;